// @ts-nocheck
import React, { Fragment, memo, useContext, useEffect, useState } from "react";
import { List, ListItem, ListItemText, Collapse, Checkbox, Divider } from "@mui/material"
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  FilterBox,
  StyledBox,
  StyledException,
  StyledFormControlLabel,
  StyledItem,
  StyledLabel,
  StyledListItem,
  StyledListLabel,
  StyledShowLess,
  StyledShowMore,
} from "./filterComponent.style";
import { ShowMoreIcon } from "../../commonComponents/Icon";
import Box from "component/layoutComponents/Box/Box";
import HyperLink from "../../commonComponents/hyperLink/hyperLink";
import { BpCheckedIcon } from "../../commonComponents/multipleSelect/multipleSelect.style";
import { BpIcon } from "../../commonComponents/multipleSelect/multipleSelect.style";
import Typography from "component/commonComponents/Typography/Typography";
import { ThemeContext } from "styled-components";
import { handleKeyPress } from "utils/functions";

interface IChildItem {
  label: string;
  id: string;
  count: number;
}

interface IListItem {
  label: string;
  childs?: IChildItem[];
  multiple: boolean;
  keyName: string;
}

export interface IUptimeListViewSelect {
  data: IListItem[];
  selectedItems: { [k: string]: IChildItem[] };
  disable: boolean;
  onChange: (selectedList: IListItem[], key: string) => void;
  defaultExpanded?: boolean;
  locale?: object;
  showCount?: boolean;
  title?: string;
  showMoreText?: string;
  showLessText?: string;
  background?: string;
  totalItemFilter?: number;
  onShowMore?: (key: string) => void;
  id?: string;
  isCheckException?: boolean;
  handleChangeException?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isCheckExceptionRequest?: boolean;
  disableCheckExceptionButton?: boolean;
};

const ListViewSelect: React.FC<IUptimeListViewSelect> = ({
  data,
  selectedItems,
  onChange,
  disable,
  defaultExpanded = false,
  locale,
  showCount,
  title = "filter by",
  showMoreText = "show more",
  showLessText = "show less",
  background,
  totalItemFilter = 5,
  onShowMore,
  id,
  isCheckException,
  handleChangeException,
  isCheckExceptionRequest,
  disableCheckExceptionButton = false,
}) => {
  const [checkedItems, setCheckedItems] = useState<{
    [k: string]: IChildItem[];
  }>(selectedItems || {});
  
  const [openItems, setOpenItems] = useState<{ [k: string]: boolean }>({});
  const [isViewMores, setIsViewMores] = useState<{ [k: string]: boolean }>({});
  const theme = useContext(ThemeContext);

  useEffect(() => {
    if (defaultExpanded) return;
    const selectedKeys = Object.keys(selectedItems);
    const openedValue = selectedKeys.reduce((total, currentKey) => {
      return {
        ...total,
        [currentKey]: selectedItems?.[currentKey]?.length > 0,
      };
    }, {});
    setOpenItems(openedValue);
  }, []);

  useEffect(() => {
    if (defaultExpanded) {
      const openedValue = data?.reduce((total, currentItem) => {
        const currentKey = currentItem?.keyName;
        return {
          ...total,
          [currentKey]: true,
        };
      }, {}) || {};
      setOpenItems(openedValue);
    }
  }, [data, defaultExpanded, selectedItems]);

  useEffect(() => {
    if (!data?.length) {
      setCheckedItems({});
      return;
    }
    setCheckedItems(selectedItems);
  }, [data?.length, selectedItems]);

  const isChecked = (childItem: IChildItem, key: string): boolean => {
    if (checkedItems[key]?.length > 0) {
      return !!checkedItems[key].find((i) => i.id === childItem.id || i.id === childItem.label);
    }
    return false;
  };

  const handleClick = (key: string) => {
    const hasKey = Object.keys(openItems).includes(key);
    setOpenItems((prev) => {
      const toggleValue = hasKey ? !prev[key] : !hasKey
      return {
        ...prev,
        [key]: toggleValue
      }
    });
  };

  const handleCheckboxChange = (
    item: IChildItem,
    key: string,
    multiple: boolean,
    uncheckable: boolean = true,
  ): void => {
    let selectedList: IChildItem[] = [];
    selectedList = multiple
      ? checkedItems[key]
        ? checkedItems[key].concat([item])
        : [item]
      : [item];

    const indexSelected: number = (checkedItems[key] || []).findIndex(
      (checkedItem: IChildItem) => checkedItem.id === item.id || checkedItem.id === item.label
    );
    if (indexSelected !== -1 && uncheckable) {
      selectedList = [...checkedItems[key]];
      selectedList.splice(indexSelected, 1);
    }

    setCheckedItems({ ...checkedItems, [key]: [...selectedList] });
    onChange(selectedList, key);
  };

  const handleViewMore = (key: string) => {
    onShowMore?.(key);
    if (isViewMores[key]) {
      delete isViewMores[key];
      setIsViewMores({ ...isViewMores });
      return;
    }
    setIsViewMores({ ...isViewMores, [key]: true });
  };

  const viewMoreBtn = (length: number, key: string) => {
    if (length <= totalItemFilter) return;
    return !isViewMores[key] ? (
      <StyledShowMore tabIndex={0} onKeyDown={(e) => handleKeyPress(e, 'Enter', () => handleViewMore(key))} onClick={() => handleViewMore(key)}>
        <ShowMoreIcon width="16" />
        <HyperLink tabIndex={-1} kind="span" label={showMoreText} />
      </StyledShowMore>
    ) : (
      <StyledShowLess tabIndex={0} onKeyDown={(e) => handleKeyPress(e, 'Enter', () => handleViewMore(key))} onClick={() => handleViewMore(key)}>
        <ShowMoreIcon width="16" />
        <HyperLink tabIndex={-1} kind="span" label={showLessText} />
      </StyledShowLess>
    );
  };

  const createListItem = (
    items: IListItem[],
    viewMoreBtn: any,
    isOpen: boolean,
    parentKey: string,
    multiple: boolean,
    uncheckable?: boolean,
  ): JSX.Element[] => {
    return (isViewMores[parentKey] ? items : items.slice(0, totalItemFilter)).map(
      (item: IChildItem, index) => {
        const label = (
          <StyledLabel>
            {locale?.[item?.id] || item.label}
            {showCount && <span>{` (${item.count})`}</span>}
          </StyledLabel>
        );
        return (
          <Box marginBottom="5px" key={item?.id}>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List>
                <ListItem>
                  {multiple ? (
                    <StyledBox>
                      <StyledFormControlLabel
                      disabled={disable}
                      control={
                        <Checkbox
                          checked={isChecked(item, parentKey)}
                          color="primary"
                          onChange={() =>
                            handleCheckboxChange(item, parentKey, multiple)
                          }
                          onKeyDown={(e) => handleKeyPress(e, 'Enter', () => handleCheckboxChange(item, parentKey, multiple))}
                        />
                        }
                        label={label}
                        id={`chk-filtercomponent-${item?.id?.toLowerCase() || item?.label?.toLowerCase()}`}
                      />
                    </StyledBox>

                  ) : (
                    <StyledBox>
                        <StyledFormControlLabel
                          disabled={disable}
                          control={
                            <Checkbox
                              checked={isChecked(item, parentKey)}
                              color="primary"
                              checkedIcon={<BpCheckedIcon />}
                              icon={<BpIcon />}
                              onChange={() =>
                                handleCheckboxChange(item, parentKey, multiple, uncheckable)
                              }
                              onKeyDown={(e) => handleKeyPress(e, 'Enter', () => handleCheckboxChange(item, parentKey, multiple))}
                          />
                        }
                        label={label}
                        id={`chk-filtercomponent-${item?.id?.toLowerCase() || item?.label?.toLowerCase()}`}
                      />
                    </StyledBox>

                  )}
                </ListItem>
                {(items.length - 1 === index ||
                  (!isViewMores[parentKey] && index === totalItemFilter - 1)) &&
                  viewMoreBtn(items.length, parentKey)}
              </List>
            </Collapse>
          </Box>
        );
      }
    );
  };

  return (
    <FilterBox background={background} id={id}>
      {isCheckExceptionRequest && (
        <StyledException isCheckException={isCheckException}>
          <StyledFormControlLabel
            control={
              <Checkbox
                checked={isCheckException}
                color="primary"
                onChange={handleChangeException}
              />
            }
            sx={{ alignItems: 'flex-start !important' }}
            disabled={disableCheckExceptionButton}
            label={locale?.exceptionMessage}
            id={`chk-filtercomponent-exception`}
          />
        </StyledException>
      )}
      {!isCheckException && (
        <>
          <Typography
            kind="h6"
            fontWeight="bold"
            fontSize={`${theme.uptimeFontSizes.desktop.fz4}px`}
            textTransform="uppercase"
            tabIndex={0}
          >
            {title}
          </Typography>
          <StyledListItem>
            {data.map((item, index) => (
              <Fragment key={item.keyName}>
                <StyledItem>
                  <Divider />
                </StyledItem>
                <StyledItem id={`${item.multiple ? 'chk' : 'rad'}-filter-${item.keyName}`}>
                  {item.label && (<StyledListLabel>
                    <ListItem tabIndex={0} onKeyDown={(e) => handleKeyPress(e, 'Enter', () => handleClick?.(item.keyName))} onClick={() => handleClick(item.keyName)} sx={{ textAlign: 'start' }}>
                      <ListItemText primary={item.label} />
                      {openItems[item.keyName] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </ListItem>
                  </StyledListLabel>)}

                  {!!item.childs &&
                    item.childs.length > 0 &&
                    createListItem(
                      item.childs,
                      viewMoreBtn,
                      openItems[item.keyName],
                      item.keyName,
                      item.multiple,
                      item.uncheckable,
                    )}
                </StyledItem>
              </Fragment>
            ))}
          </StyledListItem>
        </>
      )}
    </FilterBox>
  );
};

export const defaultProps = {
  onChange: () => {},
  selectedItems: {},
  disable: false,
  defaultExpanded: false,
  showCount: true,
};

ListViewSelect.defaultProps = defaultProps;

export default memo(ListViewSelect);
